import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/auth";
import { PILLARS } from "@/constants";
import {
  Plus, Pencil, Trash2, ArrowLeft, X, Save, CheckCircle2,
  SlidersHorizontal, CheckSquare, ListChecks, Type, FileText, AlertTriangle,
  ToggleLeft, ToggleRight, GripVertical,
} from "lucide-react";
import Modal from "@/components/Modal";

const TYPE_META = {
  scale:           { label: "Escala 0–10",      icon: SlidersHorizontal, color: "var(--lala-blue)",     hint: "Slider continuo. Lo capturado va directo al scorecard como score 0–10." },
  yes_no:          { label: "Sí / No / N-A",    icon: CheckSquare,       color: "#E4002B",              hint: "Sí = 10 · No = 0 · N/A no afecta. Ideal para confirmar políticas/planes." },
  multiple_choice: { label: "Opción múltiple",  icon: ListChecks,        color: "#D4A437",              hint: "Tú defines opciones y el score asignado a cada una. Útil para rangos." },
  open:            { label: "Texto abierto",    icon: Type,              color: "#009639",              hint: "Respuesta libre. Sirve como contexto cualitativo. No alimenta score." },
};

const EVIDENCE_META = {
  none:     { label: "Sin evidencia",   color: "bg-stone-100 text-stone-700 border-stone-300" },
  optional: { label: "Evidencia opcional", color: "bg-amber-50 text-amber-900 border-amber-300" },
  required: { label: "Evidencia OBLIGATORIA", color: "bg-red-50 text-red-800 border-red-300" },
};

export default function QuestionsAdmin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [subKpis, setSubKpis] = useState([]);
  const [editing, setEditing] = useState(null); // question being edited or "new"
  const [filter, setFilter] = useState({ pillar: "", q: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role !== "admin") nav("/dashboard");
  }, [user, nav]);

  const refresh = () =>
    Promise.all([
      http.get("/questions", { params: { active_only: false } }),
      http.get("/sub-kpis"),
    ]).then(([q, s]) => {
      setQuestions(q.data || []);
      setSubKpis(s.data || []);
    });

  useEffect(() => { refresh().catch(() => {}); }, []);

  const subKpisByPillar = useMemo(() => {
    const g = {};
    PILLARS.forEach((p) => { g[p.key] = []; });
    (subKpis || []).forEach((s) => {
      if (!g[s.pillar]) g[s.pillar] = [];
      g[s.pillar].push(s);
    });
    return g;
  }, [subKpis]);

  const subKpiById = useMemo(
    () => Object.fromEntries((subKpis || []).map((s) => [s.id, s])),
    [subKpis]
  );

  const filtered = useMemo(() => {
    return (questions || []).filter((q) => {
      if (filter.pillar && q.pillar_key !== filter.pillar) return false;
      if (filter.q && !q.text.toLowerCase().includes(filter.q.toLowerCase())) return false;
      return true;
    });
  }, [questions, filter]);

  const grouped = useMemo(() => {
    const g = {};
    PILLARS.forEach((p) => { g[p.key] = []; });
    filtered.forEach((q) => {
      if (!g[q.pillar_key]) g[q.pillar_key] = [];
      g[q.pillar_key].push(q);
    });
    return g;
  }, [filtered]);

  const handleSave = async (payload) => {
    setErr(""); setMsg("");
    try {
      if (editing && editing.id) {
        await http.put(`/admin/questions/${editing.id}`, payload);
        setMsg("Pregunta actualizada.");
      } else {
        await http.post("/admin/questions", payload);
        setMsg("Pregunta creada.");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`¿Eliminar la pregunta "${q.text.slice(0, 60)}…"?`)) return;
    setErr(""); setMsg("");
    try {
      await http.delete(`/admin/questions/${q.id}`);
      setMsg("Pregunta eliminada.");
      await refresh();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (q) => {
    try {
      await http.put(`/admin/questions/${q.id}`, { active: !q.active });
      await refresh();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin/configuracion" data-testid="back-config"
        className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>

      <div className="mt-2 mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">cuestionario 360°</div>
          <h1 className="font-display text-4xl">Editor de preguntas</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
            Las preguntas que el proveedor responderá cada período. Define tipo, pilar, evidencia y peso — todo sin tocar código.
          </p>
        </div>
        <button
          data-testid="new-question-btn"
          onClick={() => setEditing({})}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus size={16} /> Nueva pregunta
        </button>
      </div>

      {/* How it works helper */}
      <div className="sketch-border bg-[var(--cream-soft)] p-5 mb-8" data-testid="how-it-works">
        <div className="font-display text-lg text-[var(--lala-blue)] mb-3">¿Cómo se compone una pregunta?</div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Helper title="Tipo de respuesta" body="Escala 0–10, Sí/No/NA, Opción múltiple (con score por opción) o Texto abierto." />
          <Helper title="Pilar + sub-KPI" body="Indica a qué dimensión del scorecard alimenta. Sub-KPI es opcional pero recomendado para precisión." />
          <Helper title="Evidencia documental" body="Ninguna · Opcional · Obligatoria. Si es obligatoria, el proveedor no puede enviar sin adjuntar." />
          <Helper title="Alimenta scorecard" body="Si está activo, lo respondido cambia el score del pilar. Apágalo para preguntas de contexto cualitativo." />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Filtrar por pilar</label>
          <select value={filter.pillar} onChange={(e) => setFilter({ ...filter, pillar: e.target.value })}
            className="input-field mt-1 w-48" data-testid="filter-pillar">
            <option value="">Todos los pilares</option>
            {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Buscar</label>
          <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Texto de la pregunta…" className="input-field mt-1 w-full" data-testid="filter-q" />
        </div>
        <div className="text-sm text-[var(--ink-soft)]" data-testid="total-count">
          {filtered.length} pregunta{filtered.length === 1 ? "" : "s"}
        </div>
      </div>

      {msg && <div className="mb-4 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">{msg}</div>}
      {err && <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">{err}</div>}

      <div className="space-y-6">
        {PILLARS.map((p) => {
          const qs = grouped[p.key] || [];
          if (qs.length === 0) return null;
          return (
            <div key={p.key} data-testid={`pillar-block-${p.key}`}>
              <div className="font-display text-2xl text-[var(--lala-blue)] mb-3 inline-flex items-center gap-2">
                {p.label}
                <span className="text-xs font-semibold uppercase tracking-wider bg-[var(--lala-blue)] text-[var(--cream)] px-2 py-0.5 rounded-full">{qs.length}</span>
              </div>
              <div className="space-y-3">
                {qs.map((q) => (
                  <QuestionRow key={q.id}
                    q={q}
                    subKpi={q.sub_kpi_id ? subKpiById[q.sub_kpi_id] : null}
                    onEdit={() => setEditing(q)}
                    onDelete={() => handleDelete(q)}
                    onToggle={() => toggleActive(q)} />
                ))}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-[var(--ink-soft)] italic">Aún no hay preguntas que coincidan. Crea una nueva con el botón de arriba.</div>
        )}
      </div>

      {editing !== null && (
        <QuestionEditor
          question={editing}
          subKpisByPillar={subKpisByPillar}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function Helper({ title, body }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 size={14} className="text-[var(--lala-blue)] mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[var(--ink-soft)]">{body}</div>
      </div>
    </div>
  );
}

function QuestionRow({ q, subKpi, onEdit, onDelete, onToggle }) {
  const meta = TYPE_META[q.type] || TYPE_META.scale;
  const Icon = meta.icon;
  const evMeta = EVIDENCE_META[q.evidence_mode] || EVIDENCE_META.optional;
  return (
    <div className={`sketch-border-soft bg-[var(--cream)] p-4 flex items-start gap-3 ${q.active ? "" : "opacity-60"}`}
      data-testid={`question-row-${q.id}`}>
      <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: meta.color }}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: meta.color, color: "white" }}>{meta.label}</span>
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${evMeta.color}`}>
            {evMeta.label}
          </span>
          {q.feeds_scorecard ? (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[var(--lala-blue-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]/30 inline-flex items-center gap-1">
              <CheckCircle2 size={10} /> Alimenta scorecard
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 border border-stone-300">
              Contexto cualitativo
            </span>
          )}
          {subKpi && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300">
              sub-KPI · {subKpi.label}
            </span>
          )}
          {!q.active && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-stone-200 text-stone-700 border border-stone-400">
              Inactiva
            </span>
          )}
        </div>
        <div className="font-semibold text-[var(--ink)]">{q.text}</div>
        {q.help_text && <div className="text-xs text-[var(--ink-soft)] italic mt-0.5">{q.help_text}</div>}
        {q.type === "multiple_choice" && (q.options || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {q.options.map((o, i) => (
              <span key={i} className="px-2 py-0.5 rounded-md border border-[var(--lala-blue)]/30 bg-white">
                {o.label}{o.score != null ? <span className="text-[var(--ink-soft)]"> · score {o.score}</span> : null}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button onClick={onToggle} data-testid={`toggle-${q.id}`}
          title={q.active ? "Desactivar" : "Activar"}
          className="text-[var(--lala-blue)] hover:bg-[var(--lala-blue-soft)] rounded p-1.5">
          {q.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
        </button>
        <button onClick={onEdit} data-testid={`edit-${q.id}`}
          className="text-[var(--lala-blue)] hover:bg-[var(--lala-blue-soft)] rounded p-1.5"
          title="Editar">
          <Pencil size={16} />
        </button>
        <button onClick={onDelete} data-testid={`delete-${q.id}`}
          className="text-red-700 hover:bg-red-50 rounded p-1.5"
          title="Eliminar">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

function QuestionEditor({ question, subKpisByPillar, onCancel, onSave }) {
  const isNew = !question.id;
  const [form, setForm] = useState(() => ({
    text: question.text || "",
    type: question.type || "scale",
    pillar_key: question.pillar_key || "calidad",
    sub_kpi_id: question.sub_kpi_id || "",
    evidence_mode: question.evidence_mode || "optional",
    feeds_scorecard: question.feeds_scorecard !== undefined ? question.feeds_scorecard : true,
    weight: question.weight ?? 1.0,
    options: question.options || [],
    help_text: question.help_text || "",
    active: question.active !== undefined ? question.active : true,
  }));
  const [busy, setBusy] = useState(false);

  const setOption = (i, patch) => {
    const next = form.options.map((o, idx) => idx === i ? { ...o, ...patch } : o);
    setForm({ ...form, options: next });
  };
  const addOption = () => setForm({ ...form, options: [...form.options, { label: "Nueva opción", score: 5.0 }] });
  const removeOption = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        text: form.text.trim(),
        type: form.type,
        pillar_key: form.pillar_key,
        sub_kpi_id: form.sub_kpi_id || null,
        evidence_mode: form.evidence_mode,
        feeds_scorecard: !!form.feeds_scorecard,
        weight: Number(form.weight) || 0,
        options: form.type === "multiple_choice" ? form.options : null,
        help_text: form.help_text || null,
        active: !!form.active,
      };
      await onSave(payload);
    } finally { setBusy(false); }
  };

  const typeMeta = TYPE_META[form.type];
  const subKpiOpts = subKpisByPillar[form.pillar_key] || [];

  return (
    <Modal testId="question-editor-modal" backdrop="bg-black/50">
      <div className="bg-[var(--cream)] sketch-border w-full max-w-3xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div className="font-display text-2xl">{isNew ? "Nueva pregunta" : "Editar pregunta"}</div>
          <button onClick={onCancel} data-testid="editor-close" className="hover:bg-[var(--cream-soft)] p-1 rounded">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Question text */}
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Texto de la pregunta</label>
            <textarea rows={2} value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              className="input-field w-full mt-1" data-testid="edit-text"
              placeholder="¿Qué le quieres preguntar al proveedor?" />
          </div>

          {/* Type selector — visual chips */}
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Tipo de respuesta</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {Object.entries(TYPE_META).map(([k, m]) => {
                const Icon = m.icon;
                const sel = form.type === k;
                return (
                  <button key={k} type="button"
                    onClick={() => setForm({ ...form, type: k, feeds_scorecard: k === "open" ? false : form.feeds_scorecard })}
                    data-testid={`type-${k}`}
                    className={`sketch-border-soft p-3 text-left transition-all ${sel ? "ring-2 ring-offset-1" : "hover:bg-[var(--cream-soft)]"}`}
                    style={sel ? { backgroundColor: m.color, color: "white", ringColor: m.color } : {}}>
                    <Icon size={16} className={sel ? "text-white" : ""} style={!sel ? { color: m.color } : {}} />
                    <div className="font-semibold text-sm mt-1">{m.label}</div>
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-[var(--ink-soft)] mt-2 italic">{typeMeta.hint}</div>
          </div>

          {/* Options editor (multiple_choice only) */}
          {form.type === "multiple_choice" && (
            <div className="sketch-border-soft p-3 bg-[var(--cream-soft)]" data-testid="options-editor">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold mb-2">
                Opciones disponibles (con score 0–10 cada una)
              </div>
              <div className="space-y-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`opt-row-${i}`}>
                    <GripVertical size={14} className="text-stone-400" />
                    <input value={o.label}
                      onChange={(e) => setOption(i, { label: e.target.value })}
                      className="input-field flex-1" placeholder="Etiqueta de la opción" />
                    <input value={o.score ?? ""} type="number" min="0" max="10" step="0.1"
                      onChange={(e) => setOption(i, { score: e.target.value === "" ? null : Number(e.target.value) })}
                      className="input-field w-24" placeholder="Score" />
                    <button onClick={() => removeOption(i)} className="text-red-700 hover:bg-red-50 p-1.5 rounded">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addOption}
                  data-testid="add-option"
                  className="text-sm text-[var(--lala-blue)] inline-flex items-center gap-1 hover:underline mt-1">
                  <Plus size={14} /> Agregar opción
                </button>
                {form.options.length < 2 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 inline-flex items-center gap-1">
                    <AlertTriangle size={12} /> Necesitas al menos 2 opciones.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pillar & sub-KPI */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Pilar al que alimenta</label>
              <select value={form.pillar_key}
                onChange={(e) => setForm({ ...form, pillar_key: e.target.value, sub_kpi_id: "" })}
                className="input-field w-full mt-1" data-testid="edit-pillar">
                {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Sub-KPI (opcional)</label>
              <select value={form.sub_kpi_id || ""}
                onChange={(e) => setForm({ ...form, sub_kpi_id: e.target.value })}
                className="input-field w-full mt-1" data-testid="edit-sub-kpi"
                disabled={subKpiOpts.length === 0}>
                <option value="">— Pilar completo —</option>
                {subKpiOpts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Evidence + feeds + active */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Evidencia documental</label>
              <select value={form.evidence_mode}
                onChange={(e) => setForm({ ...form, evidence_mode: e.target.value })}
                className="input-field w-full mt-1" data-testid="edit-evidence-mode">
                <option value="none">Sin evidencia</option>
                <option value="optional">Opcional</option>
                <option value="required">Obligatoria</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Peso relativo</label>
              <input value={form.weight} type="number" step="0.1" min="0"
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                className="input-field w-full mt-1" data-testid="edit-weight" />
              <div className="text-xs text-[var(--ink-soft)] mt-0.5">0 = no afecta · 1.0 = peso estándar</div>
            </div>
            <div className="flex flex-col gap-1">
              <ToggleField label="Alimenta scorecard"
                checked={form.feeds_scorecard}
                onChange={(v) => setForm({ ...form, feeds_scorecard: v })}
                testid="edit-feeds" />
              <ToggleField label="Activa"
                checked={form.active}
                onChange={(v) => setForm({ ...form, active: v })}
                testid="edit-active" />
            </div>
          </div>

          {/* Help text */}
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Texto de ayuda (opcional)</label>
            <input value={form.help_text}
              onChange={(e) => setForm({ ...form, help_text: e.target.value })}
              className="input-field w-full mt-1" data-testid="edit-help"
              placeholder="Pista o instrucción que verá el proveedor debajo de la pregunta" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--cream-soft)]">
          <button onClick={onCancel} data-testid="editor-cancel"
            className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-4 py-1.5 text-sm font-semibold">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || !form.text.trim() || (form.type === "multiple_choice" && form.options.length < 2)}
            data-testid="editor-save"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={14} /> {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ToggleField({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="accent-[var(--lala-blue)]" />
      <span>{label}</span>
    </label>
  );
}
